// src/pages/Menu.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { gsap } from 'gsap';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 40px auto;
  padding: 0 20px;
`;

/* ---------- NAV (desktop + mobile) ---------- */
const NavWrap = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

/* Desktop tabs */
const TabBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 14px;

  @media (max-width: 860px) {
    display: none;
  }
`;

const Tab = styled.button`
  background: transparent;
  border: none;
  font-size: 1rem;
  color: #ff761a;
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  &:hover { opacity: 0.85; }
  &.active { border-color: #ff761a; }
`;

/* Mobile bar + dropdown */
const MobileBar = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 860px) {
    display: flex;
  }
`;

const BrandLink = styled(Link)`
  color: #ff761a;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.05rem;
`;

const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: #ff761a;
  border: 1px solid #ff761a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
`;

const DropMenu = styled.div`
  position: absolute;
  top: 42px;
  left: 0;
  right: 0;
  background: rgba(13,13,13,0.95);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 8px;
  z-index: 10;
  display: ${p => (p.open ? 'block' : 'none')};

  @media (min-width: 861px) {
    display: none;
  }
`;

const DropItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #ff761a;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.04); }
`;

const DropLink = styled(Link)`
  display: block;
  color: #ff761a;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  &:hover { background: rgba(0,0,0,0.04); }
`;

/* ---------- LIST / GRID ---------- */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  border: 1px solid #ff761a;
  padding: 20px;
  border-radius: 8px;
  background: #fff;
`;

const CategoryHeader = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid #ff761a;
  padding-bottom: 5px;
  color: #ff761a;
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ItemRow = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed #444;
  &:last-child { border-bottom: none; }
`;

const ItemName = styled.span`
  color: #111111;
`;

const ItemPrice = styled.span`
  color: #ff761a;
`;

const AddButton = styled.button`
  margin-left: 12px;
  background: #ff761a;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-weight: 700;
  padding: 4px 10px;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const AddedNote = styled.span`
  color: #ff761a;
  font-size: 0.9rem;
  margin-left: 8px;
`;

/* ---------- Signature Bowls (styled) ---------- */
const SigSection = styled.section`
  margin: 12px auto 24px;
  max-width: 1000px;
`;

const SigHeading = styled.h2`
  text-align: center;
  margin: 0 0 12px;
  font-size: 1.8rem;
`;

const SigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 16px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SigCard = styled.div`
  border: 1px solid #ff761a;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
`;

const SigName = styled.h3`
  margin: 0 0 8px;
  font-family: 'Lobster', cursive;
  font-size: 1.5rem;
  color: #111;
`;

const SigDesc = styled.p`
  margin: 0;
  line-height: 1.5;
`;

const MenuImage = styled.img`
  display: block;
  width: 100%;
  max-width: 1000px;
  margin: 18px auto 8px;
  height: auto;
`;

const MenuNote = styled.div`
  text-align: center;
  color: #555;
  margin-bottom: 20px;
`;

/* ---------- helpers ---------- */
const formatPrice = (p) => {
  if (typeof p === 'number') return `$${p.toFixed(2)}`;
  const cleaned = String(p).trim();
  if (/^\$/i.test(cleaned)) return cleaned;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? `$${num.toFixed(2)}` : cleaned;
};

// case-insensitive match for “Drinks” page title
const isDrinksPage = (title) => /drink/i.test(title || '');

/* ---------- component ---------- */
export default function Menu() {
  const [menuData, setMenuData] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const { addItem } = useCart();
  const [addedMap, setAddedMap] = useState({});
  const sigRefs = useRef([]);

  // mobile menu state
  const [open, setOpen] = useState(false);

  const onAdd = (name, price) => {
    addItem(name, price);
    setAddedMap((m) => ({ ...m, [name]: true }));
    setTimeout(() => setAddedMap((m) => ({ ...m, [name]: false })), 900);
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/pages').then(r => r.json()),
      fetch('/api/menu').then(r => r.json())
    ]).then(([pagesData, menuData]) => {
      setPages(pagesData);
      setMenuData(menuData);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (sigRefs.current.length) {
      gsap.set(sigRefs.current, { opacity: 0, y: 12 });
      gsap.to(sigRefs.current, { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'power2.out', delay: 0.1 });
    }
  }, []);

  // close dropdown on resize up to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 860) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const current = pages[pageIndex];
  const visibleData = menuData.filter(cat =>
    current?.categories?.includes(cat.category)
  );

  const hideAddButtons = isDrinksPage(current?.title);

  const pickPage = (idx) => {
    setPageIndex(idx);
    setOpen(false);
  };

  const signatureBowls = [
    {
      name: 'Local',
      desc:
        'Ahi Tuna marinated in House Sauce and Sesame Oil (3 Scoops) w/ Masago, Onions, Macadamia Nuts, topped with Furikake, Fried Garlic, and Shichimi drizzled with Unagi Sauce. Sides include Seaweed Salad and Crab Salad.',
    },
    {
      name: 'Heights',
      desc:
        'Ahi Tuna and Salmon marinated in Poke Sauce and Sesame Oil (3 Scoops of Protein and 2 Sides) w/ Masago, White Onions, Sunomono topped with Spicy Mayo, Furikake and Green Onion. Sides include Seaweed Salad and Crab Salad.',
    },
    {
      name: 'Main',
      desc:
        'Salmon marinated in Spicy Mayo and Sriracha (3 Scoops of Protein and 2 Sides) w/ Masago, Green Onions, Avocado, Furikake, Fried Garlic, Fried Onion topped with Additional Furikake, and Shichimi. Sides include Seaweed Salad and Crab Salad.',
    },
    {
      name: 'Washington',
      desc:
        'Ahi Tuna, Salmon, and Tako marinated in Sweet Chili Sauce and Sesame Oil (3 Scoops of Protein and 2 Sides) w/ Masago, Shitake Mushroom, Fried Onion, Sunomono topped with Sesame Seeds. Sides include Seaweed Salad and Crab Salad.',
    },
  ];

  return (
    <PageContainer>
      <SigSection>
        <SigHeading>– Signature Bowls –</SigHeading>
        <SigGrid>
          {signatureBowls.map((b, i) => (
            <SigCard key={b.name} ref={el => (sigRefs.current[i] = el)}>
              <SigName>{b.name}</SigName>
              <SigDesc>{b.desc}</SigDesc>
            </SigCard>
          ))}
        </SigGrid>
        <MenuImage src="/LocalPokeMenu.jpg" alt="Local Poke Menu" />
        <MenuNote>Due to economic impacts prices in stores may vary</MenuNote>
      </SigSection>
    </PageContainer>
  );
}
